/**
 * Employee Management Types
 *
 * Shared types for employee creation, updates, and responses.
 * Handles the complex User/Employment relationship where:
 * - User: Global entity, unique by email
 * - Employment: Company-tenant-specific link between User and Company
 */
import { z } from "zod";
import { uuidSchema, emailSchema, phoneSchema } from "./validation-common.js";
// ===== ENUMS =====
/**
 * Employment type enum matching Prisma EmploymentType
 * Not exported to avoid conflict with employmentGroup.ts EmploymentTypeSchema
 */
const EmploymentTypeValueSchema = z.enum([
    "FULL_TIME",
    "PART_TIME",
    "CONTRACT",
    "TEMPORARY",
    "INTERN",
]);
// ===== JOB ROLE ASSIGNMENT =====
/**
 * Job role assignment with optional pay rate
 */
export const JobRoleAssignmentSchema = z.object({
    jobRoleId: uuidSchema,
    basePayRate: z.number().positive().optional(),
});
// ===== CREATE EMPLOYEE INPUT =====
/**
 * Input for creating a new employee
 *
 * Behavior varies based on email existence:
 * - Email doesn't exist: Create User + Employment
 * - Email exists (different company): Create Employment only, reuse User
 * - Email exists (same company, active): ERROR - ALREADY_EMPLOYED
 * - Email exists (same company, inactive): Reactivate Employment
 *
 * Note: companyId is extracted from JWT, NOT included in request body
 */
export const CreateEmployeeInputSchema = z.object({
    // User fields (required for new users)
    email: emailSchema,
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    phone: phoneSchema.optional(),
    preferredName: z.string().max(100).optional(),
    // Employment fields
    employmentType: EmploymentTypeValueSchema.optional(),
    employmentStartDate: z.string().datetime().optional(),
    companyEmail: emailSchema.optional(),
    employeeId: z.string().max(100).optional(), // External system ID
    payrollId: z.string().max(100).optional(), // Payroll system identifier
    // Assignments
    locationIds: z.array(uuidSchema).optional(),
    jobRoleAssignments: z.array(JobRoleAssignmentSchema).optional(),
});
// ===== UPDATE EMPLOYEE INPUT =====
/**
 * Input for updating an existing employee
 *
 * All fields are optional - only update fields that are provided.
 * Use null to clear nullable fields (phone, preferredName, dates).
 */
export const UpdateEmployeeInputSchema = z.object({
    // User fields (updates the linked User)
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
    phone: phoneSchema.nullable().optional(),
    preferredName: z.string().max(100).nullable().optional(),
    // Employment fields
    employmentType: EmploymentTypeValueSchema.nullable().optional(),
    employmentStartDate: z.string().datetime().nullable().optional(),
    employmentEndDate: z.string().datetime().nullable().optional(),
    companyEmail: emailSchema.nullable().optional(),
    employeeId: z.string().max(100).nullable().optional(),
    payrollId: z.string().max(100).nullable().optional(),
    isActive: z.boolean().optional(),
    // Personal info fields (updates the linked UserPersonalInfo)
    birthday: z.string().datetime().nullable().optional(),
    // Assignments (additive/removal)
    addLocationIds: z.array(uuidSchema).optional(),
    removeLocationIds: z.array(uuidSchema).optional(),
    addJobRoleAssignments: z.array(JobRoleAssignmentSchema).optional(),
    removeJobRoleIds: z.array(uuidSchema).optional(),
});
// ===== EMPLOYEE RESPONSE =====
/**
 * Location info in employee response
 */
export const EmployeeLocationInfoSchema = z.object({
    id: uuidSchema,
    name: z.string().nullable(),
    city: z.string(),
    state: z.string(),
});
/**
 * Job role info in employee response
 */
export const EmployeeJobRoleInfoSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    basePayRate: z.number().nullable(),
});
/**
 * Full employee detail response
 * Combines User and Employment data
 */
export const EmployeeDetailResponseSchema = z.object({
    // User ID and Employment ID
    id: uuidSchema, // User ID (for backwards compatibility)
    employmentId: uuidSchema,
    // User fields
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    preferredName: z.string().nullable(),
    phone: z.string().nullable(),
    photoUrl: z.string().nullable(),
    // Employment fields
    employmentType: EmploymentTypeValueSchema.nullable(),
    employmentStartDate: z.string().datetime().nullable(),
    employmentEndDate: z.string().datetime().nullable(),
    companyEmail: z.string().nullable(),
    employeeId: z.string().nullable(), // External system ID
    payrollId: z.string().nullable(), // Payroll system identifier
    isActive: z.boolean(),
    // Relations
    locations: z.array(EmployeeLocationInfoSchema),
    jobRoles: z.array(EmployeeJobRoleInfoSchema),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ===== REPORTING HIERARCHY =====
/**
 * A member in a reporting hierarchy (manager or direct report)
 */
export const ReportingHierarchyMemberSchema = z.object({
    employmentId: uuidSchema,
    userId: uuidSchema,
    firstName: z.string(),
    lastName: z.string(),
    preferredName: z.string().nullable(),
    email: z.string().nullable(),
    photoUrl: z.string().nullable(),
});
/**
 * Full reporting hierarchy for an employment
 */
export const ReportingHierarchyResponseSchema = z.object({
    employmentId: uuidSchema,
    managers: z.array(ReportingHierarchyMemberSchema),
    directReports: z.array(ReportingHierarchyMemberSchema),
});
